import { Injectable } from '@angular/core';
import { LemsarApiClient } from '@lemsar/api/client';
import { ResponseTransformer } from '@lemsar/api/transformers/response';
import { Observable } from 'rxjs/Observable';
import { StoreData } from '@shared/module/common/model/store-data';
import { UI } from '@shared/models/ui';

/**
 *  @author     Arif Setianto <arifsetiantoo@gmail.com>
 *  @created    17/06/2018
 */
@Injectable()
export class RegisterService {
    private _ui: StoreData<UI>;

    public constructor(private api: LemsarApiClient) {
        this._ui = new StoreData<UI>(new UI());
    }

    public get ui$(): Observable<UI> {
        return this._ui.stream$;
    }

    public get ui(): UI {
        return this._ui.value;
    }

    public registerGovernment(data: any): Observable<any> {
        this._ui.value.loading = true;

        const formData: any = new FormData();

        formData.append('username', data.username);
        formData.append('email', data.email);
        formData.append('password', data.password);
        formData.append('name', data.name);
        formData.append('institution_name', data.institution_name);
        formData.append('owner', data.owner);
        formData.append('address', data.address);
        formData.append('phone', data.phone);

        if (data.fax) {
            formData.append('fax', data.fax);
        }

        formData.append('institution_email', data.institution_email);

        if (data.website) {
            formData.append('website', data.website);
        }

        formData.append('institution_type', data.institution_type.id);
        formData.append('postal_code', data.postal_code);
        formData.append('condition', 'operational');
        formData.append('since', data.since.getFullYear());

        formData.append('ministry', data.ministry.id);
        formData.append('type', data.type.id);
        formData.append('echelon', data.echelon.id);
        formData.append('sotk_number', data.sotk_number);

        if (data.sotk_file) {
            formData.append('sotk_file', data.sotk_file, data.sotk_file.name);
        }

        return this.api.post<any>('government-institutions', formData).pipe(ResponseTransformer.toData());
    }

    public registerPrivate(data: any): Observable<any> {
        this._ui.value.loading = true;

        const formData: any = new FormData();

        formData.append('username', data.username);
        formData.append('email', data.email);
        formData.append('password', data.password);
        formData.append('name', data.name);
        formData.append('institution_name', data.institution_name);
        formData.append('owner', data.owner);
        formData.append('address', data.address);
        formData.append('phone', data.phone);

        if (data.fax) {
            formData.append('fax', data.fax);
        }

        formData.append('institution_email', data.institution_email);

        if (data.website) {
            formData.append('website', data.website);
        }

        formData.append('institution_type', data.institution_type.id);
        formData.append('postal_code', data.postal_code);
        formData.append('condition', 'operational');
        formData.append('since', data.since.getFullYear());
        formData.append('license_number', data.license_number);

        if (data.license_file) {
            formData.append('license_file', data.license_file, data.license_file.name);
        }

        formData.append('tax_number', data.tax_number);

        if (data.tax_file) {
            formData.append('tax_file', data.tax_file, data.tax_file.name);
        }

        formData.append('pic_identity_number', data.pic_identity_number);

        if (data.wlkp_code) {
            formData.append('wlkp_code', data.wlkp_code);
        }

        return this.api.post<any>('private-institutions', formData).pipe(ResponseTransformer.toData());
    }

    public registerIndustrial(data: any): Observable<any> {
        this._ui.value.loading = true;

        const formData: any = new FormData();

        formData.append('username', data.username);
        formData.append('email', data.email);
        formData.append('password', data.password);
        formData.append('name', data.name);
        formData.append('institution_name', data.institution_name);
        formData.append('owner', data.owner);
        formData.append('address', data.address);
        formData.append('phone', data.phone);

        if (data.fax) {
            formData.append('fax', data.fax);
        }

        formData.append('institution_email', data.institution_email);

        if (data.website) {
            formData.append('website', data.website);
        }

        formData.append('postal_code', data.postal_code);
        formData.append('condition', 'operational');
        formData.append('since', data.since.getFullYear());
        formData.append('license_number', data.license_number);

        if (data.license_file) {
            formData.append('license_file', data.license_file, data.license_file.name);
        }

        formData.append('tax_number', data.tax_number);

        if (data.tax_file) {
            formData.append('tax_file', data.tax_file, data.tax_file.name);
        }

        formData.append('pic_identity_number', data.pic_identity_number);

        if (data.wlkp_code) {
            formData.append('wlkp_code', data.wlkp_code);
        }

        return this.api.post<any>('industrial-institutions', formData).pipe(ResponseTransformer.toData());
    }

    public registerCommunity(data: any): Observable<any> {
        this._ui.value.loading = true;

        const formData: any = new FormData();

        formData.append('username', data.username);
        formData.append('email', data.email);
        formData.append('password', data.password);
        formData.append('name', data.name);
        formData.append('institution_name', data.institution_name);
        formData.append('owner', data.owner);
        formData.append('address', data.address);
        formData.append('phone', data.phone);

        if (data.fax) {
            formData.append('fax', data.fax);
        }

        formData.append('institution_email', data.institution_email);

        if (data.website) {
            formData.append('website', data.website);
        }

        formData.append('institution_type', data.institution_type.id);
        formData.append('postal_code', data.postal_code);
        formData.append('condition', 'operational');
        formData.append('since', data.since.getFullYear());
        formData.append('license_number', data.license_number);

        if (data.license_file) {
            formData.append('license_file', data.license_file, data.license_file.name);
        }

        formData.append('tax_number', data.tax_number);

        if (data.tax_file) {
            formData.append('tax_file', data.tax_file, data.tax_file.name);
        }

        formData.append('pic_identity_number', data.pic_identity_number);

        if (data.wlkp_code) {
            formData.append('wlkp_code', data.wlkp_code);
        }

        return this.api.post<any>('community-institutions', formData).pipe(ResponseTransformer.toData());
    }
}



// WEBPACK FOOTER //
// ./src/lemsar/auth/domains/services/register.service.ts